import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import axios from 'axios';
import MapRadiusPicker from '../components/MapRadiusPicker';
import {
  ConfirmDialog,
  EmptyState,
  FilterBar,
  FormDialog,
  LoadingState,
  PageHeader,
  ResponsiveTable,
  StatusChip,
  useFeedback,
} from '../components/ui';
import { readSessionUser } from '../utils/sessionUser';
import { organizationCreatedNotice } from '../utils/organizationWelcome';

import { API_BASE_URL } from '../config/apiBase';

const emptyOrganization = {
  nome: '',
  cnpj: '',
  email: '',
  endereco: '',
  latitude: null,
  longitude: null,
  radius_m: '',
};

function isBlank(value) {
  return value === null || value === undefined || value === '';
}

function geofenceState(org) {
  const latBlank = isBlank(org.latitude);
  const lngBlank = isBlank(org.longitude);
  const radiusBlank = isBlank(org.radius_m);
  if (latBlank && lngBlank && radiusBlank) {
    return { key: 'absent', label: 'Não configurada' };
  }
  const radius = Number(org.radius_m);
  if (!latBlank && !lngBlank && !radiusBlank && !Number.isNaN(radius) && radius > 0) {
    return { key: 'ready', label: `Configurada · ${radius} m` };
  }
  return { key: 'invalid', label: 'Incompleta' };
}

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function Organizations() {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const { notify } = useFeedback();
  const session = readSessionUser();
  const isAdmin = session?.tipo === 'admin';
  const isManager = session?.tipo === 'gerente';
  const canCreate = isAdmin;
  const canDelete = isAdmin;
  const showUserCount = isAdmin || isManager;

  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [organization, setOrganization] = useState(emptyOrganization);
  const [submitting, setSubmitting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchOrganizations = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/organizations`, { headers: authHeaders() });
      setOrganizations(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      const status = error?.response?.status;
      setOrganizations([]);
      setLoadError(status === 401
        ? 'Sessão expirada. Entre novamente.'
        : 'Não foi possível carregar as organizações.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const canEdit = (org) => {
    if (isAdmin) return true;
    if (isManager) return Number(session?.organizacao_id) === Number(org.id);
    return false;
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return organizations;
    return organizations.filter((org) =>
      [org.nome, org.cnpj, org.email, org.endereco]
        .some((field) => String(field || '').toLowerCase().includes(term))
    );
  }, [organizations, search]);

  const openCreate = () => {
    setOrganization(emptyOrganization);
    setFormOpen(true);
  };

  const openEdit = (org) => {
    setOrganization({
      ...org,
      radius_m: org.radius_m ?? '',
      latitude: org.latitude ?? null,
      longitude: org.longitude ?? null,
    });
    setFormOpen(true);
  };

  const handleChange = (event) => {
    setOrganization((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const saveOrganization = async () => {
    if (!organization.nome?.trim() || !organization.cnpj?.trim() || !organization.email?.trim() || !organization.endereco?.trim()) {
      notify('Nome, CNPJ, e-mail e endereço são obrigatórios.', 'warning');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(organization.email.trim())) {
      notify('Informe um e-mail válido.', 'warning');
      return;
    }

    const latitude = isBlank(organization.latitude) ? null : Number(organization.latitude);
    const longitude = isBlank(organization.longitude) ? null : Number(organization.longitude);
    const radius = isBlank(organization.radius_m) ? null : Number(organization.radius_m);
    const hasLatitude = latitude != null && !Number.isNaN(latitude);
    const hasLongitude = longitude != null && !Number.isNaN(longitude);
    const hasRadius = radius != null && !Number.isNaN(radius) && radius > 0;
    if ((hasLatitude || hasLongitude || (organization.radius_m !== '' && organization.radius_m != null))
      && !(hasLatitude && hasLongitude && hasRadius)) {
      notify('Informe latitude, longitude e raio juntos, ou limpe a área. Não há coordenada nem raio padrão.', 'warning');
      return;
    }

    const payload = {
      nome: organization.nome.trim(),
      cnpj: organization.cnpj.trim(),
      email: organization.email.trim(),
      endereco: organization.endereco.trim(),
      latitude: hasLatitude ? latitude : null,
      longitude: hasLongitude ? longitude : null,
      radius_m: hasRadius ? radius : null,
    };

    setSubmitting(true);
    try {
      const baseUrl = `${API_BASE_URL}/organizations`;
      if (organization.id) {
        await axios.put(`${baseUrl}/${organization.id}`, payload, { headers: authHeaders() });
        notify('Organização atualizada.', 'success');
      } else {
        const response = await axios.post(baseUrl, payload, { headers: authHeaders() });
        const notice = organizationCreatedNotice(response?.data?.welcome);
        notify(notice.message, notice.severity);
      }
      setFormOpen(false);
      setOrganization(emptyOrganization);
      fetchOrganizations();
    } catch (error) {
      const status = error?.response?.status;
      notify(status === 401
        ? 'Sessão expirada. Entre novamente.'
        : (error?.response?.data?.message || 'Não foi possível salvar a organização.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/organizations/${pendingDelete.id}`, { headers: authHeaders() });
      notify('Organização removida.', 'success');
      setPendingDelete(null);
      fetchOrganizations();
    } catch (error) {
      notify(error?.response?.data?.message || 'Não foi possível remover a organização.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        title="Organizações"
        subtitle="Cadastro e área de presença. O servidor decide se a marcação está dentro da cerca."
        actions={canCreate ? (
          <Button variant="contained" onClick={openCreate}>Nova organização</Button>
        ) : null}
      />

      {loading ? <LoadingState label="Carregando organizações" /> : null}
      {!loading && loadError ? (
        <EmptyState title={loadError} action={<Button onClick={fetchOrganizations}>Tentar novamente</Button>} />
      ) : null}
      {!loading && !loadError ? (
        <>
          <FilterBar onSubmit={(event) => event.preventDefault()}>
            <TextField
              label="Nome, CNPJ ou e-mail"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              size="small"
              sx={{ flex: '1 1 240px', minWidth: { xs: '100%', sm: 240 } }}
            />
          </FilterBar>
          {filtered.length === 0 ? (
            <EmptyState title={organizations.length === 0 ? 'Nenhuma organização disponível.' : 'Nenhuma organização encontrada.'} />
          ) : compact ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {filtered.map((org) => (
                <OrgCard
                  key={org.id}
                  org={org}
                  showUserCount={showUserCount}
                  canEdit={canEdit(org)}
                  canDelete={canDelete}
                  onEdit={() => openEdit(org)}
                  onDelete={() => setPendingDelete(org)}
                />
              ))}
            </Box>
          ) : (
            <ResponsiveTable minWidth={860} label="Organizações">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>CNPJ</TableCell>
                  <TableCell>E-mail</TableCell>
                  <TableCell>Endereço</TableCell>
                  <TableCell>Cerca</TableCell>
                  {showUserCount ? <TableCell>Usuários ativos</TableCell> : null}
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((org) => {
                  const fence = geofenceState(org);
                  return (
                    <TableRow key={org.id} hover>
                      <TableCell>{org.nome}</TableCell>
                      <TableCell>{org.cnpj || '—'}</TableCell>
                      <TableCell>{org.email || '—'}</TableCell>
                      <TableCell>{org.endereco || '—'}</TableCell>
                      <TableCell>
                        <StatusChip status={fence.key === 'ready' ? 'success' : fence.key === 'invalid' ? 'warning' : 'default'} label={fence.label} />
                      </TableCell>
                      {showUserCount ? <TableCell>{formatUserCount(org.user_count)}</TableCell> : null}
                      <TableCell>
                        <OrgActions
                          org={org}
                          canEdit={canEdit(org)}
                          canDelete={canDelete}
                          onEdit={() => openEdit(org)}
                          onDelete={() => setPendingDelete(org)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </ResponsiveTable>
          )}
        </>
      ) : null}

      <FormDialog
        open={formOpen}
        maxWidth="md"
        title={organization.id ? 'Editar organização' : 'Nova organização'}
        onClose={() => { if (!submitting) setFormOpen(false); }}
        onSubmit={saveOrganization}
        submitting={submitting}
        submitLabel={submitting ? 'Salvando...' : 'Salvar'}
      >
        <TextField label="Nome" name="nome" value={organization.nome || ''} onChange={handleChange} required fullWidth />
        <TextField label="CNPJ" name="cnpj" value={organization.cnpj || ''} onChange={handleChange} required fullWidth />
        <TextField label="E-mail" name="email" type="email" value={organization.email || ''} onChange={handleChange} required fullWidth />
        <TextField label="Endereço" name="endereco" value={organization.endereco || ''} onChange={handleChange} required fullWidth />
        <Typography variant="body2" color="text.secondary">
          Estado da cerca: {geofenceState(organization).label}. Ausência total permite o ponto sem bloqueio geográfico. Configuração parcial é recusada pelo servidor.
        </Typography>
        <MapRadiusPicker
          key={organization.id || 'nova'}
          value={organization}
          onChange={(next) => setOrganization((prev) => ({ ...prev, ...next }))}
        />
        <Typography variant="body2" color="text.secondary">
          Preencha o raio junto com a marcação. Vazio, com latitude e longitude vazias, mantém a cerca ausente.
        </Typography>
        <Button variant="outlined" onClick={() => setOrganization((prev) => ({ ...prev, latitude: null, longitude: null, radius_m: '' }))}>
          Limpar área
        </Button>
      </FormDialog>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Remover organização"
        description={pendingDelete ? `Remover ${pendingDelete.nome}? Esta ação usa a exclusão já existente da API.` : ''}
        confirmLabel="Remover"
        destructive
        confirming={deleting}
        onConfirm={confirmDelete}
        onClose={() => { if (!deleting) setPendingDelete(null); }}
      />
    </Box>
  );
}

function formatUserCount(value) {
  return typeof value === 'number' ? String(value) : '—';
}

function OrgCard({ org, showUserCount, canEdit, canDelete, onEdit, onDelete }) {
  const fence = geofenceState(org);
  return (
    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper', minWidth: 0 }}>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>{org.nome}</Typography>
      <Typography variant="body2" color="text.secondary">{org.cnpj || 'CNPJ não informado'}</Typography>
      <Typography variant="body2" color="text.secondary">{org.email || '—'}</Typography>
      <Typography variant="body2" color="text.secondary">{org.endereco || '—'}</Typography>
      {showUserCount ? (
        <Typography variant="body2" color="text.secondary">Usuários ativos: {formatUserCount(org.user_count)}</Typography>
      ) : null}
      <Box sx={{ my: 1 }}>
        <StatusChip status={fence.key === 'ready' ? 'success' : fence.key === 'invalid' ? 'warning' : 'default'} label={fence.label} />
      </Box>
      <OrgActions org={org} canEdit={canEdit} canDelete={canDelete} onEdit={onEdit} onDelete={onDelete} />
    </Box>
  );
}

function OrgActions({ org, canEdit, canDelete, onEdit, onDelete }) {
  if (!canEdit && !canDelete) return <Typography variant="caption" color="text.secondary">Somente leitura</Typography>;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {canEdit ? <Button size="small" variant="outlined" onClick={onEdit} aria-label={`Editar ${org.nome}`}>Editar</Button> : null}
      {canDelete ? <Button size="small" color="error" variant="contained" onClick={onDelete} aria-label={`Remover ${org.nome}`}>Remover</Button> : null}
    </Box>
  );
}
